/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { useMutation } from '@apollo/client'
import { createSearchParams } from 'react-router-dom'
import { FieldArray } from 'react-final-form-arrays'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { Form, FormValues } from 'pagesNew/OrginizeBlogPages/OrganizeCreateSuccessStory/Form'
import { CREATE_STUDY_GUIDE_SECTION } from 'queries/studyGuide'
import { createStudyGuideSection, createStudyGuideSectionVariables } from '__generatedTypes__/createStudyGuideSection'
import { QUERY_PARAM, STUDY_GUIDE_TAB } from 'pagesNew/BlogPages'
import { findAllStudyGuide_findAllStudyGuide_items as StudyGuide } from '__generatedTypes__/findAllStudyGuide'
import { validateRequiredField } from 'utils/validation'
import { toast } from 'utils/toast'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { BROADCAST_CHANNEL } from 'constants/consts'
import { REFETCH_STUDY_GUIDES_MESSAGE } from 'pagesNew/StudyGuide/Create'
import { Props as StudyGuidesFieldArrayProps, StudyGuidesFieldArray } from './Form/StudyGuidesFieldArray'

type Props = {
  platform: Platform
}

export const OrganizeCreateStudyGuide: FC = withPlatformCheck(({ platform }: Props) => {
  const { navigate } = useLink()

  const [createSuccessStorySection] = useMutation<createStudyGuideSection, createStudyGuideSectionVariables>(
    CREATE_STUDY_GUIDE_SECTION
  )

  const handleSubmit = async (values: FormValues) => {
    try {
      const { title, description = '' } = values
      await createSuccessStorySection({
        variables: {
          createStudyGuideSectionInput: {
            title,
            description,
            platform: {
              id: platform.id,
            },
            studyGuide: values.articles.map(({ id }) => ({ id })),
            studyGuideIds: values.articles.map(({ id }) => id),
          },
        },
      })

      navigate(cmsRoutes.BLOG_PAGES, {}, { searchParams: createSearchParams({ [QUERY_PARAM]: STUDY_GUIDE_TAB.id }) })
      toast.success('Study guide section created successfully')
    } catch (error) {
      toast.error('Failed to create study guide section')
    }
  }

  useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
    if (event.data === REFETCH_STUDY_GUIDES_MESSAGE) {
      toast.success('Study guide created successfully')
    }
  })

  return (
    <Form
      platform={platform}
      onSubmit={handleSubmit}
      title="Organize Study Guide Articles"
      createLink={cmsRoutes.CREATE_STUDY_GUIDE}
      renderFields={({ form, linkParams }) => (
        <FieldArray<StudyGuide> name="articles" validate={validateRequiredField}>
          {({ fields, meta }) => (
            <StudyGuidesFieldArray
              platform={platform}
              onChange={form.change as unknown as StudyGuidesFieldArrayProps['onChange']}
              fields={fields}
              meta={meta}
              linkParams={linkParams}
            />
          )}
        </FieldArray>
      )}
    />
  )
})
